import axios, { isAxiosError, type AxiosResponse } from 'axios';
import { getAgentDashboardToken } from '@/apps/apis/claudeCodeDashboard/token';
import type {
  GigOrderAdminDetail,
  GigOrderPageSnapshotsResponse,
  GigOrderPageSnapshotView,
  GigOrderReference,
  GigPayoutRequest,
  GigPayoutResult,
  GigWorkerApproval,
  GigWorkerDetail,
  ListGigWorkersResponse,
} from '../models/GigWorker';

const REQUEST_TIMEOUT_MS = 20_000;

/** Provides internal admin operations for GIGWORK workers. */
export class GigWorkerClient {
  private readonly headers: Record<string, string>;

  constructor(
    private readonly apiUrl: string,
    apiKey: string,
  ) {
    this.headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-API-Key': apiKey,
    };
  }

  public async listWorkers(): Promise<ListGigWorkersResponse> {
    const response: AxiosResponse<ListGigWorkersResponse> = await axios.get(`${this.apiUrl}/gigwork/admin/workers`, {
      headers: this.headers,
      timeout: REQUEST_TIMEOUT_MS,
    });
    return response.data;
  }

  public async getWorkerDetail(workerId: string): Promise<GigWorkerDetail> {
    const response: AxiosResponse<GigWorkerDetail> = await axios.get(
      `${this.apiUrl}/gigwork/admin/workers/${workerId}`,
      {
        headers: this.headers,
        timeout: REQUEST_TIMEOUT_MS,
      },
    );
    return response.data;
  }

  public async getOrderDetail(orderId: string): Promise<GigOrderAdminDetail> {
    const response: AxiosResponse<GigOrderAdminDetail> = await axios.get(
      `${this.apiUrl}/gigwork/admin/orders/${orderId}`,
      {
        headers: this.headers,
        timeout: REQUEST_TIMEOUT_MS,
      },
    );
    return response.data;
  }

  /** Resolves a persisted Gigwork order by OMS order ref; a 404 means the OMS order has no Gigwork order. */
  public async getOrderReferenceByOmsOrderRef(omsOrderRef: string): Promise<GigOrderReference | null> {
    try {
      const response: AxiosResponse<GigOrderReference> = await axios.get(
        `${this.apiUrl}/gigwork/admin/orders/byOmsOrderRef/${encodeURIComponent(omsOrderRef)}`,
        {
          headers: this.headers,
          timeout: REQUEST_TIMEOUT_MS,
        },
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  public async getOrderPageSnapshots(orderId: string): Promise<GigOrderPageSnapshotView[]> {
    const response: AxiosResponse<GigOrderPageSnapshotsResponse> = await axios.get(
      `${this.apiUrl}/gigwork/admin/orders/${orderId}/pageSnapshots`,
      {
        headers: this.headers,
        timeout: REQUEST_TIMEOUT_MS,
      },
    );
    return response.data.snapshots;
  }

  /** Edits a worker's admin-only KakaoTalk name and memo (null clears a field). Returns the refreshed detail. */
  public async updateWorker(
    workerId: string,
    patch: { kakaoName: string | null; adminMemo: string | null },
  ): Promise<GigWorkerDetail> {
    const response: AxiosResponse<GigWorkerDetail> = await axios.patch(
      `${this.apiUrl}/gigwork/admin/workers/${workerId}`,
      patch,
      {
        headers: this.headers,
        timeout: REQUEST_TIMEOUT_MS,
      },
    );
    return response.data;
  }

  public async payoutWorker(workerId: string, payout: GigPayoutRequest): Promise<GigPayoutResult> {
    const token = getAgentDashboardToken();
    if (!token) {
      throw new Error('Dashboard session required to record payout. Sign in again and retry.');
    }
    const response: AxiosResponse<GigPayoutResult> = await axios.post(
      `${this.apiUrl}/gigwork/admin/workers/${workerId}/payout`,
      payout,
      {
        headers: { ...this.headers, Authorization: `Bearer ${token}` },
        timeout: REQUEST_TIMEOUT_MS,
      },
    );
    return response.data;
  }

  public async approveWorker(workerId: string): Promise<GigWorkerApproval> {
    const response: AxiosResponse<GigWorkerApproval> = await axios.post(
      `${this.apiUrl}/gigwork/admin/workers/${workerId}/approve`,
      undefined,
      {
        headers: this.headers,
        timeout: REQUEST_TIMEOUT_MS,
      },
    );
    return response.data;
  }
}
